import { useEffect, useState } from 'react';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { addPageAction, noticeError } from '../newrelic';

const emptyForm = { name: '', description: '', status: 'ACTIVE' };

export default function Items() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const fetchItems = async () => {
    try {
      const res = await api.getItems();
      setItems(res.data || []);
    } catch (err) {
      noticeError(err, { page: 'items', action: 'fetch' });
      toast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    addPageAction('items_page_viewed');
    fetchItems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await api.updateItem(editingId, form);
        addPageAction('item_updated', { itemId: editingId });
        toast('Item updated', 'success');
      } else {
        await api.createItem(form);
        addPageAction('item_created', { itemName: form.name });
        toast('Item created', 'success');
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      await fetchItems();
    } catch (err) {
      noticeError(err, { page: 'items', action: editingId ? 'update' : 'create' });
      toast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, description: item.description || '', status: item.status || 'ACTIVE' });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteItem(id);
      addPageAction('item_deleted', { itemId: id });
      toast('Item deleted', 'success');
      await fetchItems();
    } catch (err) {
      noticeError(err, { page: 'items', action: 'delete' });
      toast(err.message, 'error');
    }
  };

  const cancelEdit = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Items</h1>
          <p className="page-subtitle">CRUD operations backed by DynamoDB</p>
        </div>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Item
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <span className="card-title">{editingId ? 'Edit Item' : 'Create Item'}</span>
            <button className="btn btn-outline btn-sm" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label className="label">Name *</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Enter item name"
                />
              </div>
              <div className="form-group">
                <label className="label">Status</label>
                <select
                  className="input"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className="input"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Enter description"
              />
            </div>
            <div className="actions actions-right">
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting && <span className="spinner" />}
                {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {items.length === 0 ? (
          <div className="empty-state">
            <p>No items yet. Create one to get started.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="mono">{item.id?.substring(0, 8)}...</td>
                  <td style={{ fontWeight: 500 }}>{item.name}</td>
                  <td className="text-muted">{item.description || '—'}</td>
                  <td>
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="text-muted">
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div className="actions actions-right">
                      <button className="btn btn-outline btn-sm" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
